/* eslint-disable no-unused-vars */
import {
  IBondTradeMessage,
  SecurityIdentifierType,
  TradeMessageSubType,
  TradeMessageType,
} from "@/lib/interfaces and Enums/ENUMBondTradeMessage";
import { IBondSecurity } from "@/lib/interfaces and Enums/ENUMBondSecurity";
import { IPortfolio } from "@/lib/interfaces and Enums/ENUMPortfolio";
import { returnSettlementDateForDays } from "@/lib/businessRules/settlementDateRules";
import { returnSizeFormatted } from "@/lib/globalFormatterHelper";
import { IBondTicket } from "@/store/tradeFromScratchStore";
import {
  ITicketIssue,
  returnAllocationIssues,
  returnWireAllocations,
} from "./allocationRules";
import {
  ITicketField,
  returnRequiredTicketKeys,
  returnTicketFieldIssues,
  returnTransactionNumberDetail,
} from "./ticketSharedHelper";

// The send confirmation timings and labels are shared with the future ticket - kept
// re-exported here so existing callers do not care which file they live in.
export {
  SENT_BUTTON_CONFIRMATION_MS,
  SENT_CONFIRMATION_MS,
  returnIndicativePrice,
  returnSentAtTimeLabel,
  returnSentConfirmationRemainingMs,
} from "./ticketSharedHelper";

// LQC-2254 - pure rules for the bond ticket: what is still missing, what can be derived
// from the chosen security, and the outbound message. No React, no store reads - the
// caller passes the resolved security and portfolio in, so all of this is unit testable.

export type IBondTicketField = ITicketField<IBondTicket>;

// Picking (or clearing) the bond. A pick re-defaults settlement from its SettlementDays
// (LQC-2995); the user can still edit it after. Clearing leaves the date alone.
export function returnBondSecurityChanges(
  tradeDate: string,
  bond: IBondSecurity | undefined,
  isin: string,
): Partial<IBondTicket> {
  const changes: Partial<IBondTicket> = {
    isin,
    securityName: bond?.securityName ?? "",
  };
  if (!bond) return changes;
  return {
    ...changes,
    settlementDate: returnSettlementDateForDays(tradeDate, bond.settlementDays),
  };
}

// The fields a user must fill before the ticket can be sent, in the order they appear
// in the ticket. Labels are what the Send button reports as missing.
export const BOND_TICKET_REQUIRED_FIELDS: IBondTicketField[] = [
  { key: "isin", label: "Security" },
  { key: "portfolioCode", label: "Portfolio" },
  { key: "nominal", label: "Nominal", isNumeric: true },
  { key: "direction", label: "Direction" },
  { key: "settlementDate", label: "Settlement Date", isDate: true },
  { key: "tradeDate", label: "Trade Date", isDate: true },
];

// What the ticket marks with a "*". Derived from the list above so the two cannot
// drift apart.
export const BOND_TICKET_REQUIRED_KEYS = returnRequiredTicketKeys(
  BOND_TICKET_REQUIRED_FIELDS,
);

// Security is one choice behind two rows, so isin standing in for both is deliberate -
// they are only ever set together.
export function returnMissingBondTicketEntries(ticket: IBondTicket): ITicketIssue[] {
  return [
    ...returnTicketFieldIssues(ticket, BOND_TICKET_REQUIRED_FIELDS),
    ...returnAllocationIssues(ticket.nominal, ticket.additionalAllocations),
  ];
}

// Labels only - what the Send button reports and the toast repeats. Keys come from
// returnMissingBondTicketEntries, which the ticket uses to highlight the rows themselves.
export function returnMissingBondTicketFields(ticket: IBondTicket): string[] {
  return returnMissingBondTicketEntries(ticket).map(({ label }) => label);
}

export function isBondTicketComplete(ticket: IBondTicket): boolean {
  return returnMissingBondTicketFields(ticket).length === 0;
}

// Bond prices are quoted per 100 of face, so principal is nominal x price / 100.
// This is indicative only - it is not sent, and it excludes accrued interest, which
// cannot be computed from /Securities/bonds (no coupon, no last coupon date, no day
// count convention).
export function returnIndicativePrincipal(
  nominal: number | null,
  price: number | null,
): number | null {
  if (nominal === null || price === null) return null;
  return (nominal * price) / 100;
}

// What the confirmation line describes. Built at send time - the ticket is cleared the
// moment the POST succeeds, so nothing can be read back off it afterwards.
export function returnSentTicketSummary(ticket: IBondTicket): string {
  const nominal =
    typeof ticket.nominal === "number" && ticket.nominal > 0
      ? returnSizeFormatted(ticket.nominal)
      : "";
  return [ticket.direction, nominal, ticket.isin].filter(Boolean).join(" ");
}

export interface IBuildBondTradeMessageParams {
  ticket: IBondTicket;
  portfolio: IPortfolio | undefined;
  traderName: string;
  // Used only when the portfolio row carries no customerCode.
  fallbackEntity?: string;
}

// Returns undefined when the ticket cannot legally be sent, so the caller never has to
// decide what a half-built message means.
export function buildBondTradeMessage({
  ticket,
  portfolio,
  traderName,
  fallbackEntity = "",
}: IBuildBondTradeMessageParams): IBondTradeMessage | undefined {
  if (!isBondTicketComplete(ticket)) return undefined;
  if (!portfolio) return undefined;
  const nominal = ticket.nominal as number;

  return {
    messageType: TradeMessageType.Trade,
    subType: TradeMessageSubType.Bond,
    clientDetails: {
      originatingEntity: portfolio.customerCode || fallbackEntity,
      lei: portfolio.lei,
      traderName,
    },
    security: {
      identifierDetails: [
        {
          identifier: ticket.isin,
          identifierType: SecurityIdentifierType.ISIN,
        },
      ],
    },
    tradeDetails: {
      tradeDate: ticket.tradeDate,
      direction: ticket.direction,
      nominal,
      settlementDate: ticket.settlementDate,
      ...returnTransactionNumberDetail(ticket.transactionNumber),
    },
    allocations: returnWireAllocations(
      portfolio.portfolioCode,
      nominal,
      ticket.additionalAllocations,
    ),
  };
}
